import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiQuote, FiCheckCircle, FiMessageCircle } = FiIcons;

const testimonials = [
  {
    name: "Prof. Dr. Bambang Setiawan",
    role: "Guru Besar Universitas Indonesia",
    content: "Layanan layout jurnal dari Akademika Support sangat membantu saya dalam mengejar deadline submisi ke jurnal Q1. Hasilnya sangat rapi dan sesuai dengan template yang diminta.",
    initials: "BS",
    rating: 5
  },
  {
    name: "Dr. Maria Ulfa",
    role: "Peneliti Senior BRIN",
    content: "Sangat terkesan dengan ketelitian tim dalam melakukan editing naskah. Mereka tidak hanya memperbaiki tata bahasa, tapi juga memastikan konsistensi referensi yang sangat krusial.",
    initials: "MU",
    rating: 5
  },
  {
    name: "Andi Wijaya, M.T.",
    role: "Kandidat Doktor ITB",
    content: "Desain poster ilmiah untuk konferensi internasional saya mendapat banyak pujian. Visualisasi datanya sangat jelas dan profesional. Terima kasih Akademika Support!",
    initials: "AW",
    rating: 5
  },
  {
    name: "Siti Aminah, Ph.D.",
    role: "Dosen Universitas Gadjah Mada",
    content: "Proses koordinasi yang sangat mudah dan transparan. Saya selalu mendapatkan update rutin mengenai progres pekerjaan naskah saya. Sangat direkomendasikan.",
    initials: "SA",
    rating: 4
  },
  {
    name: "Reza Pratama",
    role: "Mahasiswa Magister Unair",
    content: "Layanan parafrase yang sangat etis. Mereka membantu memperbaiki struktur kalimat tanpa merubah substansi penelitian saya sedikitpun. Hasil turnitin turun drastis.",
    initials: "RP",
    rating: 5
  },
  {
    name: "Dr. Hendra Kurniawan",
    role: "Kepala Laboratorium Riset",
    content: "Sudah berlangganan untuk kebutuhan desain cover buku institusi kami. Kualitas estetikanya selalu premium dan mencerminkan standar akademik yang tinggi.",
    initials: "HK",
    rating: 5
  }
];

const TestimonialsPage = () => {
  return (
    <div className="bg-white font-inter">
      {/* Hero Section - Updated to a slightly deeper, elegant slate gradient for better definition */}
      <section className="bg-gradient-to-b from-slate-200/50 via-slate-100/20 to-white py-24 border-b border-divider relative overflow-hidden">
        {/* Subtle background decorative element */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-extrabold text-primary mb-8 tracking-tight">
              Testimoni Klien Kami
            </h1>
            <p className="text-xl md:text-2xl text-secondary max-w-3xl mx-auto leading-relaxed font-medium">
              Kepercayaan dari ratusan akademisi dan peneliti di seluruh Indonesia adalah kebanggaan terbesar kami.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Summary */}
      <section className="py-12 border-b border-divider bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-12 md:gap-24">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-1">4.9/5.0</div>
              <div className="flex justify-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-500 fill-current" aria-hidden="true" />
                ))}
              </div>
              <p className="text-xs font-bold text-secondary uppercase tracking-widest">Kepuasan Rata-rata</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-1">500+</div>
              <div className="flex justify-center mb-2">
                <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500" aria-hidden="true" />
              </div>
              <p className="text-xs font-bold text-secondary uppercase tracking-widest">Proyek Berhasil</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-1">100%</div>
              <div className="flex justify-center mb-2">
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-blue-500" aria-hidden="true" />
              </div>
              <p className="text-xs font-bold text-secondary uppercase tracking-widest">Respon Cepat</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-8 border border-divider rounded-2xl bg-white hover:shadow-xl transition-all duration-300 relative group"
              >
                <div className="absolute top-6 right-8 opacity-10 group-hover:opacity-20 transition-opacity">
                  <SafeIcon icon={FiQuote} className="w-12 h-12 text-primary" aria-hidden="true" />
                </div>
                <div className="flex mb-6">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon 
                      key={i} 
                      icon={FiStar} 
                      className={`w-4 h-4 ${i < item.rating ? 'text-yellow-500 fill-current' : 'text-gray-200'}`} 
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <p className="text-secondary text-lg leading-relaxed mb-8 font-normal italic">
                  "{item.content}"
                </p>
                <div className="flex items-center gap-4 pt-6 border-t border-divider">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold shrink-0">
                    {item.initials}
                  </div>
                  <div>
                    <h4 className="font-bold text-primary leading-tight">{item.name}</h4>
                    <p className="text-sm text-secondary font-medium">{item.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-primary rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-6 text-white">Bagikan Pengalaman Anda</h2>
              <p className="text-white/80 text-lg mb-10 max-w-xl mx-auto">
                Apakah Anda puas dengan layanan kami? Bantu rekan akademisi lainnya dengan memberikan ulasan Anda.
              </p>
              <button className="bg-white text-primary px-10 py-4 rounded-xl font-bold text-lg hover:bg-opacity-90 transition-all focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary">
                Tulis Testimoni
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;